import "./App.scss";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Routes, Route, useLocation } from "react-router-dom";
import { FaCalculator } from "react-icons/fa";

import AppNav from "./components/AppNav";
import EmbedCodeSection, { embedMap, initEmbedState, getActiveProps } from "./components/EmbedCodeSection";
import { routeTitles } from "./utils/staticData";

// Calculators
import BonusTaxCalculator from "./components/Calculators/BonusTaxCalculator/BonusTaxCalculator";
import CapitalGainsTaxCalculator from "./components/Calculators/CapitalGainsTaxCalculator/CapitalGainsTaxCalculator";
import CryptocurrencyTaxCalculator from "./components/Calculators/CryptoCurencyTaxCalculator/CryptoCurencyTaxCalculator";
import FederalTaxIncomeCalculator from "./components/Calculators/FederalTaxIncomeCalculator/FederalTaxIncomeCalculator";
import MortgageInterestDeductionCalculator from "./components/Calculators/MortgageInterestDeductionCalculator/MortgageInterestDeductionCalculator";
import UncleKamBusinessTaxCalculator from "./components/Calculators/ProfessionalSmallBusinessTaxCalculator/ProfessionalSmallBusinessTaxCalculator";
import RetirementSavingsTaxCalculator from "./components/Calculators/RetirementSavingsTaxCalculator/RetirementSavingsTaxCalculator";
import SelfEmploymentCalculator from "./components/Calculators/SelfEmploymentTaxCalculator/SelfEmploymentTaxCalculator";
import TaxBracketCalculator from "./components/Calculators/TaxBracketCalculator/TaxBracketCalculator";
import HomeOfficeCalculator from "./components/Calculators/HomeOfficeCalculator/HomeOfficeCalculator";

// Forms
import MultiStepLeadForm from "./components/Forms/MultiStepLeadGeneration/MultiStepLeadGeneration";
import GhlLeadForm from "./components/Forms/GhlLeadForm/GhlLeadForm";
import BookAppointmentFrom from "./components/Forms/BookAppointmentForm/BookAppointmentFrom";
import BookAppointmentFromUpdatdUi from "./components/Forms/BookAppointmentFormUpdatedUi/BookAppointmentFormUpdatedUi";
import BookAppointmentFormV3 from "./components/Forms/BookAppointmentFormV3/BookAppointmentFormV3";
import OneStepOrderForm from "./components/Forms/OneStepOrderForm/OneStepOrderForm";
import TwoStepOrderForm from "./components/Forms/TwoStepOrderForm/TwoStepOrderForm";
import CourseOrderForm from "./components/Forms/CourseOrderForm/CourseOrderForm";
import UpsellOrderForm from "./components/Forms/UpsellOrderForm/UpsellOrderForm";
import LeadGenerationForm from "./components/Forms/LeadGenerationForm/LeadGenerationForm";
import QuestionnaireForm from "./components/Forms/QuestionnaireForm/QuestionnaireForm";
import RedirectButton from "./components/Forms/RedirectButton/RedirectButton";

// Lists
import TaxPros from "./components/Lists/TaxPros/TaxPros";
import ProCard from "./components/Lists/ProCard/ProCard";

const DEFAULT_TITLE = "Tax Calculator";
const REDIRECT_URL = "https://unclekam.com/";

function AppHeader({ title, isNavOpen, setIsNavOpen }) {
  return (
    <header className="py-3 border-bottom bg-white">
      <div className="container d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-3">
          <div
            className="bg-primary text-white rounded p-2 d-flex align-items-center justify-content-center"
            style={{ width: 40, height: 40 }}
          >
            <FaCalculator />
          </div>

          <div>
            <h5 className="mb-0 fw-bold text-primary">{title}</h5>
            <small className="text-muted">Uncle Kam</small>
          </div>
        </div>

        <AppNav isOpen={isNavOpen} setIsOpen={setIsNavOpen} />

        <div className="text-end">
          <div className="text-primary fw-semibold small">
            Professional Tax Guidance
          </div>
          <div className="text-muted small">Accurate Tax Calculations</div>
        </div>
      </div>
    </header>
  );
}

function AppFooter() {
  return (
    <footer className="bg-primary-900 text-light py-4 mt-5 w-full">
      <div className="container text-center">
        <h5 className="fw-bold">Uncle Kam</h5>
        <p className="mb-2 text-grey-200 fs-7" style={{ fontSize: "13px" }}>
          Professional, licensed, and vetted tax advisors who will save you money
        </p>
        <small className="text-grey-400 small mb-0" style={{ fontSize: "10px" }}>
          This calculator provides estimates only. Consult a tax professional for
          personalized advice.
        </small>
      </div>
    </footer>
  );
}

function HomePageButton({ onOpenNav }) {
  return (
    <div className="d-flex flex-column justify-content-center align-items-center flex-grow-1 h-full gap-3 text-center my-auto py-5">
      <h2 className="fw-bold text-dark mb-1">Uncle Kam Calculators &amp; Forms</h2>
      <p className="text-muted mb-3" style={{ maxWidth: 520 }}>
        Test and embed forms and calculators. Click below to open the new Course Order Form directly.
      </p>
      <div className="d-flex gap-3 flex-wrap justify-content-center">
        <a
          href="/course-order-form"
          className="btn btn-lg btn-success fw-bold px-4 py-3 shadow-sm text-decoration-none d-flex align-items-center gap-2"
        >
          <span>🎓</span> Open Course Order Form
        </a>
        <button className="btn btn-lg btn-primary fw-bold px-4 py-3" onClick={onOpenNav}>
          ☰ Browse All Forms &amp; Calculators
        </button>
      </div>
    </div>
  );
}

function App() {
  const { pathname, search } = useLocation();
  const [isNavOpen, setIsNavOpen] = useState(false);

  // URL params — stable reference parsed once per search string change
  const urlParams = useMemo(() => {
    const params = {};
    new URLSearchParams(search).forEach((v, k) => { params[k] = v; });
    return params;
  }, [search]);

  // Embed attribute state — lifted so it can drive live props
  const [embedChecked, setEmbedChecked] = useState({});
  const [embedValues, setEmbedValues] = useState({});

  // Reset embed state when route changes, pre-check attrs that match URL params
  useEffect(() => {
    const { checked, values } = initEmbedState(pathname);
    const info = embedMap[pathname];
    if (info) {
      for (const attr of info.attrs) {
        if (urlParams[attr.name] !== undefined) {
          checked[attr.name] = true;
          values[attr.name] = urlParams[attr.name];
        }
      }
    }
    setEmbedChecked(checked);
    setEmbedValues(values);
  }, [pathname, urlParams]);

  const handleToggle = useCallback((name) => {
    setEmbedChecked((prev) => {
      const next = { ...prev, [name]: !prev[name] };
      // When title_prefix is toggled, sync title_font_size and title_color
      if (name === "title_prefix") {
        next.title_font_size = next.title_prefix;
        next.title_color = next.title_prefix;
      }
      return next;
    });
  }, []);

  const handleValueChange = useCallback((name, val) => {
    setEmbedValues((prev) => ({ ...prev, [name]: val }));
  }, []);

  // Active overrides from embed checkboxes
  const liveProps = useMemo(
    () => getActiveProps(pathname, embedChecked, embedValues),
    [pathname, embedChecked, embedValues]
  );

  const title = useMemo(() => {
    return routeTitles[pathname] || DEFAULT_TITLE;
  }, [pathname]);

  // Keep routes in one place so the JSX below stays clean
  const routes = useMemo(
    () => [
      // Calculators
      { path: "/federal-tax", element: <FederalTaxIncomeCalculator year={2026} {...liveProps} /> },
      { path: "/business-tax", element: <UncleKamBusinessTaxCalculator {...liveProps} /> },
      { path: "/crypto-tax", element: <CryptocurrencyTaxCalculator {...liveProps} /> },
      {
        path: "/capital-gains",
        element: <CapitalGainsTaxCalculator year={2026} {...liveProps} />,
      },
      { path: "/tax-brackets", element: <TaxBracketCalculator {...liveProps} /> },
      { path: "/bonus-tax", element: <BonusTaxCalculator year={2025} {...liveProps} /> },
      { path: "/retirement-savings", element: <RetirementSavingsTaxCalculator {...liveProps} /> },
      {
        path: "/mortgage-deduction",
        element: <MortgageInterestDeductionCalculator year={2026} {...liveProps} />,
      },
      { path: "/self-employment-tax", element: <SelfEmploymentCalculator {...liveProps} /> },
      { path: "/home-office", element: <HomeOfficeCalculator {...liveProps} /> },


      // Forms
      {
        path: "/multi-step-lead-generation",
        element: <MultiStepLeadForm redirectUrl={REDIRECT_URL} {...liveProps} />,
      },
      { path: "/simple-lead-form", element: <GhlLeadForm {...liveProps} /> },
      { path: "/book-appointment", element: <BookAppointmentFrom {...liveProps} /> },
      { path: "/book-appointment-v2", element: <BookAppointmentFromUpdatdUi {...liveProps} /> },
      { path: "/book-appointment-v3", element: <BookAppointmentFormV3 {...liveProps} /> },
      {
        path: "/one-step-order-form",
        element: (
          <OneStepOrderForm productIds="1,2" additionalProductIds="1" {...liveProps} />
        ),
      },
      {
        path: "/two-step-order-form",
        element: <TwoStepOrderForm productIds="1,2" additionalProductIds="1" {...liveProps} />,
      },
      {
        path: "/course-order-form",
        element: (
          <CourseOrderForm
            offers="ofr_9bf57ag8,ofr_bxwjn67y"
            bumpOffers="ofr_bb6zyysg"
            {...liveProps}
          />
        ),
      },
      { path: "/upsell-order-form", element: <UpsellOrderForm productId="2" {...liveProps} /> },
      {
        path: "/lead-generation-form",
        element: <LeadGenerationForm redirectUrl={REDIRECT_URL} {...liveProps} />,
      },
      {
        path: "/questionnaire-form",
        element: (
          <QuestionnaireForm data_source="test" redirectUrl={REDIRECT_URL} {...liveProps} />
        ),
      },
       {
        path: "/redirect-button",
        element: (
          <RedirectButton email="taxpro@maildrop.cc"  buttonText="Redirect" redirect_url={REDIRECT_URL} qa_mode={'on'} {...liveProps} />
        ),
      },

      // Lists
      { path: "/tax-pros", element: <TaxPros {...liveProps} /> },
      { path: "/pro-card", element: <ProCard {...liveProps} /> },
    ],
    [liveProps]
  );

  return (
    <div className="d-flex flex-column min-vh-100">
      <AppHeader
        title={title}
        isNavOpen={isNavOpen}
        setIsNavOpen={setIsNavOpen}
      />

      <main className="container py-4 flex-grow-1 d-flex flex-column">
        <Routes>
          <Route
            path="/"
            element={<HomePageButton onOpenNav={() => setIsNavOpen(true)} />}
          />
          {routes.map((r) => (
            <Route key={r.path} path={r.path} element={r.element} />
          ))}
        </Routes>
        {pathname !== "/" && (
          <EmbedCodeSection
            pathname={pathname}
            checked={embedChecked}
            values={embedValues}
            onToggle={handleToggle}
            onValueChange={handleValueChange}
          />
        )}
      </main>

      <AppFooter />
    </div>
  );
}

export default App;
